//Singles Day, Black Friday, Cyber Monday, Boxing Day, Winter Sale Hero Template for 2025

import { InverseHeader } from '@jouzen/ecom-components/layout';
import { Button } from '@jouzen/ecom-components/v3/Button';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { Price } from '@jouzen/ecom-components/v3/Price';
import { Tag } from '@jouzen/ecom-components/v3/Tag';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import FullScreenHero from '@/app/components/FullScreenHero';
import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import useCurrency from '@/app/hooks/useCurrency';
import type { SupportedCurrency } from '@/app/types/currency';

import StatsigHomeHeroShopChiclet from '../HomeHeroShopChiclet';
import HomeHeroWithChicletWrapper from '../HomeHeroWithChicletWrapper';
import HsaFsaIcon from './common/HsaFsaIcon';
import { SinglesDayHeroBackground } from './common/SinglesDayHeroBackground';

//we are currenlty hardcoding these values in statsig for 2025 campaign
export type DiscountDifferenceData = Record<SupportedCurrency, number>;

interface HolidayHero25Props {
  readonly discountDifferenceData?: DiscountDifferenceData;
  readonly tagMsgKey?: MessageKey;
  readonly titleMsgKey: MessageKey;
  readonly subcopyMsgKey: MessageKey;
  readonly buttonProps: {
    href: string;
    ctaLabel: MessageKey;
    onClick?: () => void;
  };
  readonly Background?: () => JSX.Element;
  readonly displayChiclet?: boolean;
  readonly dataCy?: string;
}

const HolidayHero25 = ({
  discountDifferenceData,
  tagMsgKey,
  titleMsgKey,
  subcopyMsgKey,
  buttonProps,
  Background = SinglesDayHeroBackground,
  displayChiclet = true,
  dataCy,
}: HolidayHero25Props) => {
  const t = useTranslations();
  const { currencyCode } = useCurrency();

  const tValues = discountDifferenceData
    ? {
        price: () => {
          const price =
            currencyCode in discountDifferenceData
              ? discountDifferenceData[currencyCode]
              : discountDifferenceData.USD;
          return <Price price={price} currencyCode={currencyCode} />;
        },
      }
    : undefined;

  return (
    <HomeHeroWithChicletWrapper data-cy="holiday-hero-wrapper">
      <InverseHeader exit={true}>
        <div
          className="relative overflow-x-clip bg-sandstone-200"
          data-cy={dataCy ?? 'holiday-hero-container'}
        >
          <FullScreenHero
            Background={Background}
            className="h-full overflow-hidden"
          >
            <GridItem
              className="z-20 flex h-svh flex-col pb-8 md:pb-20"
              colEnd={{ sm: 'main', md: 12, lg: 11 }}
              colStart={{ sm: 'main' }}
              rowEnd={{ sm: 2 }}
              rowStart={{ sm: 1 }}
            >
              <div className="flex h-full flex-col justify-end md:justify-around">
                <div className="mt-auto md:mt-37">
                  {tagMsgKey && (
                    <div
                      className="flex justify-center pb-4 md:justify-start"
                      data-cy="holiday-tag-eyebrow"
                    >
                      <Tag size="medium" variant="black">
                        {t(tagMsgKey)}
                      </Tag>
                    </div>
                  )}
                  <Typography
                    className="pb-4 text-center text-5xl leading-tighter tracking-tighter text-balance md:text-left md:text-6xl lg:text-6xl xl:text-[80px] xl:tracking-tight"
                    Element="h1"
                    font="serif"
                    data-cy="holiday-hero-title"
                  >
                    <RichText messageKey={titleMsgKey} tValues={tValues} />
                  </Typography>
                </div>
              </div>
              <Motion
                animate={{ opacity: 1 }}
                data-cy="holiday-content"
                initial={{ opacity: 0 }}
                transition={{ duration: 1, type: 'tween' }}
              >
                <div className="flex flex-col items-center md:items-start">
                  <Typography
                    align="centerMdLeft"
                    className="mb-4 text-xl text-balance md:mb-6 md:text-wrap"
                    color="dark"
                    data-cy="holiday-hero-subcopy"
                    Element="p"
                    variant="body-large"
                  >
                    {t(subcopyMsgKey)}
                  </Typography>
                  <HsaFsaIcon className="mb-5 md:hidden" inverted />
                  <Button
                    size="large"
                    className="font-bold"
                    data-cy="holiday-hero-content-button"
                    variant="primary"
                    href={buttonProps.href}
                    onClick={buttonProps.onClick}
                  >
                    {t(buttonProps.ctaLabel)}
                  </Button>
                  <HsaFsaIcon className="mt-6 hidden md:block" inverted />
                </div>
              </Motion>
            </GridItem>
          </FullScreenHero>
          {displayChiclet && <StatsigHomeHeroShopChiclet />}
        </div>
      </InverseHeader>
    </HomeHeroWithChicletWrapper>
  );
};
export default HolidayHero25;
